import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Store, User, AlertCircle, Send } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { listActiveCasesForDesigner, listApplicationsByDesigner, getCase } from "../services/cases";
import { getRatingSummary } from "../services/reviews";
import { getUnreadMap } from "../services/reads";
import { CaseCard } from "../components/CaseCard";
import { RatingSummary } from "../components/StarRating";
import { StatusBadge } from "../components/StatusBadge";
import { Button } from "../components/ui/button";
import { Skeleton } from "../components/ui/skeleton";

export default function DesignerDashboard() {
  const { t } = useTranslation();
  const { user, profile } = useAuth();
  const [active, setActive] = useState([]);
  const [applied, setApplied] = useState([]);
  const [rating, setRating] = useState({ avg: 0, count: 0 });
  const [unread, setUnread] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    (async () => {
      // Soft-fail per query (matches CaseDetail): a transient permission/timing
      // error on one read must not blow up the whole dashboard or show a scary toast.
      const [actR, appsR, sumR] = await Promise.allSettled([
        listActiveCasesForDesigner(user.uid),
        listApplicationsByDesigner(user.uid),
        getRatingSummary(user.uid),
      ]);
        const act = actR.status === "fulfilled" ? actR.value : [];
        if (actR.status === "fulfilled") { setActive(act); getUnreadMap(act, user.uid).then(setUnread).catch(() => {}); }
        if (sumR.status === "fulfilled") setRating(sumR.value);
      if (appsR.status === "fulfilled") {
        const activeIds = new Set(act.map((c) => c.id));
        const appliedCases = (
          await Promise.all(
            appsR.value
              .filter((a) => !activeIds.has(a.caseId))
              .map((a) => getCase(a.caseId).catch(() => null))
          )
        ).filter(Boolean);
        setApplied(appliedCases);
      }
      setLoading(false);
    })();
  }, [user]);

  const complete = profile?.profileComplete;
  const activeCases = active.filter((c) => c.status !== "Completed");
  const completedCases = active.filter((c) => c.status === "Completed");

  return (
    <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8" data-testid="designer-dashboard">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">{t("designer.dashboardTitle")}</h1>
          <p className="mt-1 text-muted-foreground">{t("designer.dashboardSubtitle")}</p>
          <div className="mt-3 flex items-center gap-2" data-testid="designer-rating-summary">
            <span className="text-xs font-semibold uppercase tracking-[0.08em] text-muted-foreground">{t("reviews.myRating")}:</span>
            <RatingSummary avg={rating.avg} count={rating.count} size="h-4 w-4" testid="my-rating" />
          </div>
        </div>
        <div className="flex gap-2">
          <Link to="/designer/profile">
            <Button variant="outline" className="rounded-full" data-testid="edit-profile-button">
              <User className="mr-1.5 h-4 w-4" /> {t("nav.myProfile")}
            </Button>
          </Link>
          <Link to="/marketplace">
            <Button className="rounded-full bg-brand text-white hover:bg-brand/90" data-testid="browse-cases-button">
              <Store className="mr-1.5 h-4 w-4" /> {t("designer.browseCases")}
            </Button>
          </Link>
        </div>
      </div>

      {!complete && (
        <Link to="/designer/profile" className="mt-6 flex items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800" data-testid="profile-incomplete-banner">
          <AlertCircle className="h-5 w-5 shrink-0" />
          {t("designer.profileIncomplete")}
        </Link>
      )}

      <h2 className="mb-4 mt-10 font-heading text-xl font-semibold tracking-tight">{t("designer.activeCases")}</h2>
      {loading ? (
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-72 rounded-lg" />)}</div>
      ) : activeCases.length === 0 ? (
        <Link to="/marketplace" className="block rounded-lg border border-dashed border-border p-10 text-center text-muted-foreground transition-colors hover:border-brand hover:bg-brand/5 hover:text-brand" data-testid="no-active">
          {t("designer.noActive")}
        </Link>
      ) : (
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">{activeCases.map((c, i) => <CaseCard key={c.id} caseItem={c} index={i} unreadCount={unread[c.id] || 0} netForDesigner />)}</div>
      )}

      {completedCases.length > 0 && (
        <>
          <h2 className="mb-4 mt-12 font-heading text-xl font-semibold tracking-tight">{t("designer.completedSection")}</h2>
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="designer-completed-cases">{completedCases.map((c, i) => <CaseCard key={c.id} caseItem={c} index={i} unreadCount={unread[c.id] || 0} netForDesigner />)}</div>
        </>
      )}

      <h2 className="mb-4 mt-12 font-heading text-xl font-semibold tracking-tight">{t("designer.myApplications")}</h2>
      {!loading && applied.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border p-10 text-center text-muted-foreground" data-testid="no-applications">{t("designer.noApplications")}</div>
      ) : (
        <div className="divide-y divide-border rounded-lg border border-border bg-card">
          {applied.map((c) => (
            <Link key={c.id} to={`/cases/${c.id}`} className="flex items-center justify-between gap-3 p-4 transition-colors hover:bg-secondary/50" data-testid={`applied-case-${c.id}`}>
              <div className="flex items-center gap-3">
                <Send className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium">{c.title}</span>
              </div>
              <StatusBadge status={c.status} />
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
